import { useState, useMemo, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Lightbulb, TrendingUp, Scissors, Plane, Percent, Layers, Sparkles, Package, Users, Truck } from "lucide-react";
import { cn } from "@/lib/utils";

const fmt = (v) => {
  const n = Math.round(Math.abs(v));
  const s = n >= 1000000 ? `${(n / 1000000).toFixed(2)}M` : n >= 1000 ? `${(n / 1000).toFixed(0)}K` : `${n}`;
  return `$${s}`;
};

const PERIODS = [
  { id: "month", label: "Month" },
  { id: "rolling12", label: "Rolling 12" },
  { id: "rolling24", label: "Rolling 24" },
  { id: "thisfy", label: "This FY" },
  { id: "nextfy", label: "Next FY" },
];

const LEVER_LABELS = {
  revenue: "Revenue",
  overhead: "Overhead",
  direct: "Direct Costs",
  material: "Material",
  labor: "Labor",
  freight: "Freight",
  travel: "Travel/Indirect",
  bid: "Bid Margins",
};

export function SuggestionsDialog({ open, onClose, calc, settings, data, ci, onPatch }) {
  const [period, setPeriod] = useState("rolling12");
  const [selected, setSelected] = useState(new Set());
  // Reset checkbox selection whenever period changes so a stale mix doesn't linger.
  useEffect(() => { setSelected(new Set()); }, [period]);
  const targetPct = Number.isFinite(settings?.ebitda_target_pct) ? settings.ebitda_target_pct : 10;
  const targetFrac = targetPct / 100;

  const result = useMemo(() => {
    if (!calc || !data) return null;
    const n = data.months.length;
    let a, b;
    if (period === "month") {
      [a, b] = [ci, ci + 1];
    } else if (period === "rolling12") {
      [a, b] = [ci, Math.min(ci + 12, n)];
    } else if (period === "rolling24") {
      [a, b] = [ci, Math.min(ci + 24, n)];
    } else if (period === "thisfy") {
      [a, b] = [0, Math.min(12, n)];
    } else {
      [a, b] = [Math.min(12, n), Math.min(24, n)];
    }
    const cnt = b - a;
    if (cnt <= 0) return null;
    const s = (arr) => arr.slice(a, b).reduce((x, y) => x + y, 0);
    const R = s(calc.revenue);
    const E = s(calc.ebitda);
    if (!R) return null;
    const G = targetFrac * R - E;
    const m = s(calc.gross) / R;
    const ohTot = s(calc.overheadTotal);
    const dirPct = settings.direct_costs_pct || 0;
    const curPct = (E / R) * 100;

    if (G <= 0) return { onTarget: true, curPct, R, E, cnt };

    const list = [];
    const revLeverEligible = m > targetFrac + 0.02;
    if (revLeverEligible) {
      const X = G / (m - targetFrac);
      list.push({
        leverId: "revenue", icon: TrendingUp, rel: X / R,
        title: `Increase Revenue +${fmt(X)}`,
        detail: `+${fmt(X / cnt)}/mo of new work at your current ${(m * 100).toFixed(1)}% contribution margin closes the gap (overhead stays fixed).`,
      });
    }
    const ohSorted = [...(settings.overhead_items || [])].sort((x, y) => (y.monthly || 0) - (x.monthly || 0));
    list.push({
      leverId: "overhead", icon: Scissors, rel: G / Math.max(ohTot, 1),
      title: `Decrease Overhead −${fmt(G)} (${fmt(G / cnt)}/mo)`,
      detail: `Largest levers: ${ohSorted.slice(0, 2).map((o) => `${o.name} ${fmt(o.monthly)}/mo`).join(", ")}. A ${((G / ohTot) * 100).toFixed(1)}% total overhead cut hits ${targetPct}%.`,
    });
    const pts = (G / R) * 100;
    if (dirPct - pts > 40) {
      list.push({
        leverId: "direct", icon: Percent, rel: pts / dirPct,
        title: `Cut Direct Costs from ${dirPct}% to ${(dirPct - pts).toFixed(2)}%`,
        detail: `${pts.toFixed(2)} points of buy-out savings / field productivity = ${fmt(G)} straight to EBITDA.`,
      });
    }
    // Category-specific direct-cost levers: Material, Labor, Freight.
    // If the category has enough headroom to close the whole gap → surface a full-close card.
    // Otherwise → surface a partial-close card (still selectable for a Custom Blend).
    const dItems = settings.direct_items || [];
    const findItem = (rx) => dItems.find((it) => rx.test(it.name || ""));
    const categoryLevers = [
      { leverId: "material", rx: /^material/i, icon: Package,
        detail: (cur, tgt) => `Tighter vendor negotiations, alternate sourcing or bulk buys — trim Material from ${cur.toFixed(2)}% to ${tgt.toFixed(2)}% and the gap closes.`,
        partial: (cur, floor, save) => `Down to ${floor.toFixed(2)}% (${(cur - floor).toFixed(2)} pts of buy-out savings) frees ~${fmt(save)} — pair with another lever to finish. Best used inside a Custom Blend below.` },
      { leverId: "labor", rx: /^labor$/i, icon: Users,
        detail: (cur, tgt) => `Right-size crews, prefab, better scheduling — bring Labor from ${cur.toFixed(2)}% to ${tgt.toFixed(2)}% and the gap closes.`,
        partial: (cur, floor, save) => `Down to ${floor.toFixed(2)}% (${(cur - floor).toFixed(2)} pts of productivity gain) frees ~${fmt(save)} — pair with another lever to finish. Best used inside a Custom Blend below.` },
      { leverId: "freight", rx: /^freight/i, icon: Truck,
        detail: (cur, tgt) => `Consolidated shipments, regional stocking, fewer expedited runs — cut Freight from ${cur.toFixed(2)}% to ${tgt.toFixed(2)}% and the gap closes.`,
        partial: (cur, floor, save) => `Down to ${floor.toFixed(2)}% (${(cur - floor).toFixed(2)} pts of freight savings) frees ~${fmt(save)} — pair with another lever to finish. Best used inside a Custom Blend below.` },
    ];
    for (const c of categoryLevers) {
      const item = findItem(c.rx);
      if (!item) continue;
      const cur = item.pct || 0;
      if (cur <= 0.1) continue;
      if (cur - pts >= 0.5) {
        // Full-close card
        const tgt = cur - pts;
        list.push({
          leverId: c.leverId, icon: c.icon, rel: pts / cur,
          title: `Cut ${item.name} from ${cur.toFixed(2)}% to ${tgt.toFixed(2)}%`,
          detail: c.detail(cur, tgt),
        });
      } else {
        // Partial-close card
        const floor = 0.5; // conservative floor
        if (cur <= floor) continue;
        const savePts = cur - floor;
        const saveDol = (savePts / 100) * R;
        list.push({
          leverId: c.leverId, icon: c.icon, rel: 1.5 - (saveDol / G), // sort after full-close ideas
          title: `Cut ${item.name} to ${floor.toFixed(2)}% (+${fmt(saveDol)})`,
          detail: c.partial(cur, floor, saveDol),
        });
      }
    }
    const travel = (settings.indirect_items || []).find((it) => /travel/i.test(it.name));
    if (travel && travel.pct > 1) {
      const maxSave = ((travel.pct - 1.0) / 100) * R;
      if (maxSave >= G) {
        list.push({
          leverId: "travel", icon: Plane, rel: pts / travel.pct,
          title: `Decrease ${travel.name} from ${travel.pct}% to ${(travel.pct - pts).toFixed(2)}%`,
          detail: `Travel alone can close the full ${fmt(G)} gap at current revenue.`,
        });
      } else {
        list.push({
          leverId: "travel", icon: Plane, rel: 0.9,
          title: `Decrease ${travel.name} to 1.0% (+${fmt(maxSave)})`,
          detail: `Covers ${((maxSave / G) * 100).toFixed(0)}% of the gap — pair with −${fmt((G - maxSave) / cnt)}/mo overhead to finish.`,
        });
      }
    }
    list.push({
      leverId: "bid", icon: Percent, rel: pts / Math.max(100 - dirPct, 1),
      title: `Lift bid margins +${pts.toFixed(2)} pts (Direct GM ${(100 - dirPct).toFixed(1)}% → ${(100 - dirPct + pts).toFixed(1)}%)`,
      detail: `Same volume, better pricing on new awards = ${fmt(G)} without adding a single project.`,
    });
    // Built-in 5-way blend
    const share = G / 5;
    const sharePts = (share / R) * 100;
    const blendParts = [];
    if (revLeverEligible) {
      const dx = share / (m - targetFrac);
      blendParts.push(`+${fmt(dx)} revenue (${fmt(dx / cnt)}/mo)`);
    }
    blendParts.push(`−${fmt(share)} overhead (${fmt(share / cnt)}/mo)`);
    blendParts.push(`−${sharePts.toFixed(2)} pts direct costs`);
    if (travel && travel.pct > 0) {
      blendParts.push(`−${Math.min(sharePts, travel.pct).toFixed(2)} pts ${travel.name.toLowerCase()}`);
    } else {
      blendParts.push(`−${sharePts.toFixed(2)} pts indirect`);
    }
    blendParts.push(`+${sharePts.toFixed(2)} pts bid margins`);
    list.push({
      leverId: null, icon: Layers, rel: 0.01,
      title: `Blend: 5-way split — each closes ${fmt(share)}`,
      detail: `Distributes the ${fmt(G)} gap evenly across every lever: ${blendParts.join(" · ")}. Usually the most achievable path.`,
    });

    list.sort((x, y) => x.rel - y.rel);
    return {
      onTarget: false, curPct, R, E, G, cnt,
      list: list.slice(0, 10),
      // Values needed for custom-blend recompute
      m, revLeverEligible, ohTot, dirPct, travel,
      dItems,
    };
  }, [calc, settings, data, ci, period, targetFrac, targetPct]);

  // Custom blend across only the checked levers
  const custom = useMemo(() => {
    if (!result || result.onTarget || selected.size < 2) return null;
    const { G, R, cnt, m, revLeverEligible, dirPct, travel, dItems } = result;
    const ids = Array.from(selected);
    // Filter to eligible levers only (drop revenue if margins too thin)
    const eligible = ids.filter((id) => id !== "revenue" || revLeverEligible);
    if (eligible.length < 2) return null;
    const share = G / eligible.length;
    const sharePts = (share / R) * 100;
    const findD = (rx) => (dItems || []).find((it) => rx.test(it.name || ""));
    const parts = eligible.map((id) => {
      if (id === "revenue") {
        const dx = share / (m - targetFrac);
        return `+${fmt(dx)} revenue (${fmt(dx / cnt)}/mo)`;
      }
      if (id === "overhead") return `−${fmt(share)} overhead (${fmt(share / cnt)}/mo)`;
      if (id === "direct") return `−${sharePts.toFixed(2)} pts direct costs → ${(dirPct - sharePts).toFixed(2)}%`;
      if (id === "material") {
        const it = findD(/^material/i);
        if (!it) return `−${sharePts.toFixed(2)} pts material`;
        const applied = Math.min(sharePts, Math.max((it.pct || 0) - 0.5, 0));
        return `−${applied.toFixed(2)} pts material → ${((it.pct || 0) - applied).toFixed(2)}%${applied < sharePts ? " (max)" : ""}`;
      }
      if (id === "labor") {
        const it = findD(/^labor$/i);
        if (!it) return `−${sharePts.toFixed(2)} pts labor`;
        const applied = Math.min(sharePts, Math.max((it.pct || 0) - 0.5, 0));
        return `−${applied.toFixed(2)} pts labor → ${((it.pct || 0) - applied).toFixed(2)}%${applied < sharePts ? " (max)" : ""}`;
      }
      if (id === "freight") {
        const it = findD(/^freight/i);
        if (!it) return `−${sharePts.toFixed(2)} pts freight`;
        const applied = Math.min(sharePts, Math.max((it.pct || 0) - 0.5, 0));
        return `−${applied.toFixed(2)} pts freight → ${((it.pct || 0) - applied).toFixed(2)}%${applied < sharePts ? " (max)" : ""}`;
      }
      if (id === "travel") {
        if (travel && travel.pct > 0) {
          return `−${Math.min(sharePts, travel.pct).toFixed(2)} pts ${travel.name.toLowerCase()}`;
        }
        return `−${sharePts.toFixed(2)} pts indirect`;
      }
      if (id === "bid") return `+${sharePts.toFixed(2)} pts bid margins`;
      return "";
    });
    return {
      count: eligible.length, share, ids: eligible,
      title: `Custom Blend: ${eligible.length}-way split — each closes ${fmt(share)}`,
      parts,
    };
  }, [result, selected, targetFrac]);

  const toggleLever = (id) => {
    if (!id) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const commitTarget = (raw) => {
    if (!onPatch) return;
    const v = raw === "" ? 10 : parseFloat(raw);
    if (!Number.isFinite(v) || v < 0 || v > 50) return;
    if (Math.abs(v - targetPct) < 1e-9) return;
    onPatch({ ebitda_target_pct: v });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="suggestions-dialog">
        <DialogHeader>
          <DialogTitle className="text-sm flex items-center gap-2">
            <Lightbulb className="h-4 w-4 text-amber-500" />
            Path to {targetPct.toFixed(targetPct % 1 === 0 ? 0 : 1)}% EBITDA
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex rounded-md border border-slate-200 overflow-hidden w-fit" data-testid="suggestions-period-toggle">
            {PERIODS.map((p) => (
              <button key={p.id} onClick={() => setPeriod(p.id)}
                className={cn("px-3 h-7 text-xs font-medium", period === p.id ? "bg-[#007BFF] text-white" : "bg-white text-slate-600 hover:bg-slate-50")}
                data-testid={`suggestions-period-${p.id}`}>
                {p.label}
              </button>
            ))}
          </div>
          <label className="flex items-center gap-1.5 text-[11px] text-slate-500">
            <span className="font-medium text-slate-600">EBITDA Target</span>
            <input
              type="number"
              min={0}
              max={50}
              step={0.1}
              defaultValue={targetPct}
              key={targetPct}
              onBlur={(e) => commitTarget(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
              className="w-16 h-7 text-xs text-right border border-slate-200 rounded px-1.5 focus:outline-none focus:ring-2 focus:ring-[#007BFF]"
              data-testid="suggestions-target-input"
            />
            <span className="text-slate-400">%</span>
          </label>
        </div>
        {!result ? (
          <p className="text-xs text-slate-400 py-4">No forecast data available.</p>
        ) : (
          <>
            <div className="text-xs text-slate-500 bg-slate-50 rounded-md px-3 py-2">
              Current EBITDA: <b className={result.curPct < 0 ? "text-red-600" : "text-slate-800"}>{fmt(result.E)} ({result.curPct.toFixed(1)}%)</b> on {fmt(result.R)} revenue over {result.cnt} month{result.cnt > 1 ? "s" : ""}.
              {!result.onTarget && <> Gap to {targetPct}%: <b className="text-[#007BFF]">{fmt(result.G)}</b>.</>}
            </div>
            {result.onTarget ? (
              <div className="text-sm text-emerald-700 bg-emerald-50 rounded-md px-3 py-3" data-testid="suggestions-on-target">
                Already at or above {targetPct}% EBITDA for this period. Protect it: hold overhead at {fmt(calc ? calc.overheadTotal[ci] : 0)}/mo, keep Direct GM ≥ {(100 - (settings?.direct_costs_pct || 0)).toFixed(1)}%, and keep the pipeline conversion feeding New Sales.
              </div>
            ) : (
              <>
                {custom && (
                  <div className="border border-[#007BFF]/30 bg-[#007BFF]/5 rounded-md px-3 py-2.5" data-testid="custom-blend-card">
                    <div className="flex items-center justify-between gap-3 mb-1">
                      <div className="text-[13px] font-semibold text-slate-800 flex items-center gap-1.5">
                        <Sparkles className="h-3.5 w-3.5 text-[#007BFF]" />
                        {custom.title}
                      </div>
                      <button onClick={() => setSelected(new Set())}
                        className="text-[10px] text-slate-500 hover:text-slate-700 underline"
                        data-testid="custom-blend-clear">
                        Clear
                      </button>
                    </div>
                    <div className="text-[11px] text-slate-600">
                      Uses only your picks: <b>{custom.ids.map((id) => LEVER_LABELS[id]).join(", ")}</b>.
                    </div>
                    <ul className="text-[11px] text-slate-600 mt-1 space-y-0.5">
                      {custom.parts.map((p, i) => (
                        <li key={i} className="flex gap-1.5"><span className="text-[#007BFF]">•</span>{p}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className="text-[10px] text-slate-400 flex items-center gap-2">
                  <span>Tip:</span>
                  <span>Check any 2+ ideas below to build a custom blend across only those levers.</span>
                </div>
                <ol className="space-y-2" data-testid="suggestions-list">
                  {result.list.map((sug, i) => (
                    <li key={i} className={cn(
                      "flex gap-3 items-start border rounded-md px-3 py-2.5 transition-colors",
                      sug.leverId && selected.has(sug.leverId)
                        ? "border-[#007BFF]/40 bg-[#007BFF]/5"
                        : "border-slate-200 bg-white"
                    )} data-testid={`suggestion-${i}`}>
                      {sug.leverId ? (
                        <label className="flex items-center pt-0.5 shrink-0 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selected.has(sug.leverId)}
                            onChange={() => toggleLever(sug.leverId)}
                            className="h-3.5 w-3.5 accent-[#007BFF] cursor-pointer"
                            data-testid={`suggestion-check-${sug.leverId}`}
                          />
                        </label>
                      ) : (
                        <span className="flex items-center justify-center h-5 w-5 rounded-full bg-slate-100 text-slate-400 shrink-0" title="Built-in blend">
                          <Layers className="h-3 w-3" />
                        </span>
                      )}
                      <span className="flex items-center justify-center h-6 w-6 rounded-full bg-[#007BFF]/10 text-[#007BFF] text-xs font-bold shrink-0">{i + 1}</span>
                      <div className="min-w-0">
                        <div className="text-[13px] font-semibold text-slate-800 flex items-center gap-1.5">
                          <sug.icon className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                          {sug.title}
                        </div>
                        <div className="text-[11px] text-slate-500 mt-0.5">{sug.detail}</div>
                      </div>
                    </li>
                  ))}
                </ol>
              </>
            )}
            <p className="text-[10px] text-slate-400">Each option closes the full gap on its own (except the built-in Blend, which splits it 5 ways). Numbers recompute live from the current scenario.</p>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
